/**
 * Dashboard Component
 * Tabella bollette + grafico spesa mensile
 */

import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import BillingTable from './BillingTable';
import MonthlyChart from './MonthlyChart';
import StatsCards from './StatsCards';
import BolletteUpload from './BolletteUpload';
import OfferteComparator from './OfferteComparator';
import ContrattoAttuale from './ContrattoAttuale';

function Dashboard() {
  const [bollette, setBollette] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    tipo: 'all', // 'all', 'luce', 'gas'
    dateFrom: '',
    dateTo: ''
  });

  // Fetch bollette
  useEffect(() => {
    fetchBollette();
  }, []);

  const fetchBollette = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/bollette');
      setBollette(response.data.data || []);
    } catch (err) {
      setError(err.message);
      console.error('Errore fetch bollette:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete bolletta
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa bolletta?')) return;
    
    try {
      await api.delete(`/bollette/${id}`);
      setBollette(bollette.filter(b => b.id !== id));
    } catch (err) {
      alert('Errore eliminazione: ' + err.message);
    }
  };

  // Filtered bollette
  const filteredBollette = bollette
    .filter(b => {
      if (filters.tipo !== 'all' && b.tipo !== filters.tipo) return false;
      if (filters.dateFrom && new Date(b.data_bolletta) < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && new Date(b.data_bolletta) > new Date(filters.dateTo)) return false;
      return true;
    })
    .sort((a, b) => new Date(b.data_bolletta) - new Date(a.data_bolletta));

  if (loading) {
    return <div style={{ padding: '20px' }}>Caricamento...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f' }}>
        <p>❌ Errore: {error}</p>
        <button onClick={fetchBollette}>Riprova</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '10px' }}>⚡ Energy Tracker</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Dashboard monitoraggio bollette luce e gas
      </p>

      {/* Widget Contratto Attuale */}
      <ContrattoAttuale />

      {/* Stats Cards */}
      <StatsCards bollette={bollette} />

      {/* Upload Form */}
      <div style={{ marginBottom: '30px' }}>
        <BolletteUpload onUploadSuccess={fetchBollette} />
      </div>

      {/* Offerte Comparator - Luce */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginBottom: '20px' }}>💰 Risparmio Potenziale - Luce</h2>
        <OfferteComparator tipo="luce" />
      </div>

      {/* Offerte Comparator - Gas (Issue #165: mostra solo se esistono bollette gas) */}
      {bollette.some(b => b.tipo === 'gas') ? (
        <div style={{ 
          background: '#fff', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{ marginBottom: '20px' }}>💰 Risparmio Potenziale - Gas</h2>
          <OfferteComparator tipo="gas" />
        </div>
      ) : (
        <div style={{ 
          background: '#f9f9f9', 
          padding: '20px', 
          borderRadius: '8px', 
          border: '1px dashed #ccc',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '10px', color: '#666' }}>📭 Offerte Gas</h2>
          <p style={{ color: '#999', margin: 0 }}>
            Carica una bolletta gas per vedere le offerte gas e il risparmio potenziale
          </p>
        </div>
      )}

      {/* Monthly Chart */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginBottom: '20px' }}>📈 Spesa Mensile (Ultimi 12 Mesi)</h2>
        <MonthlyChart bollette={bollette} />
      </div>

      {/* Filters */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '15px', 
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Tipo:</span>
          <select 
            value={filters.tipo}
            onChange={e => setFilters({ ...filters, tipo: e.target.value })}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="all">Tutti</option>
            <option value="luce">Luce</option>
            <option value="gas">Gas</option>
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Da:</span>
          <input 
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters({ ...filters, dateFrom: e.target.value })}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>A:</span>
          <input 
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters({ ...filters, dateTo: e.target.value })}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </label>

        {(filters.tipo !== 'all' || filters.dateFrom || filters.dateTo) && (
          <button
            onClick={() => setFilters({ tipo: 'all', dateFrom: '', dateTo: '' })}
            style={{ 
              padding: '6px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ccc',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            Reset Filtri
          </button>
        )}
      </div>

      {/* Billing Table */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '20px' }}>📋 Bollette ({filteredBollette.length})</h2>
        <BillingTable 
          bollette={filteredBollette} 
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}

export default Dashboard;
